import React from "react";
import {
	Button,
	makeStyles,
	tokens,
	Text,
} from "@fluentui/react-components";
import Modal from "./Modal";
import { useEditor } from "../editor/EditorContext";
import { featureFlags } from "@cuntas-mitool/shared";

const useStyles = makeStyles({
	content: {
		display: "flex",
		flexDirection: "column",
		gap: tokens.spacingVerticalM,
	},
	messageText: {
		lineHeight: tokens.lineHeightBase300,
	},
	planButtons: {
		display: "flex",
		flexDirection: "column",
		gap: tokens.spacingVerticalS,
	},
	actions: {
		display: "flex",
		gap: tokens.spacingHorizontalS,
	},
	loginActions: {
		display: "flex",
		flexDirection: "column",
		gap: tokens.spacingVerticalS,
		width: "100%",
	},
	microsoftBtn: {
		backgroundColor: "#0078d4",
		color: "white",
		":hover": { backgroundColor: "#106ebe" },
	},
	googleBtn: {
		backgroundColor: "#ea4335",
		color: "white",
		":hover": { backgroundColor: "#d33426" },
	},
});

interface PaywallModalProps {
	/** Whether the modal is open */
	isOpen: boolean;
	/** Whether the user is logged in */
	isAuthenticated: boolean;
	/** Callback when modal should close */
	onClose: () => void;
	/** Callback when user clicks a plan - will open Stripe checkout for that tier */
	onSubscribe: (priceTier: 'annual' | 'monthly') => void;
	/** Callback when user clicks Login with Microsoft */
	onLogin: () => void;
	/** Callback when user clicks Login with Google */
	onLoginGoogle?: () => void;
	/** Override the modal title (defaults to "Trial Expired") */
	title?: string;
}

/**
 * PaywallModal component - shows when trial has expired and user tries to insert.
 * Displays subscription information and options to subscribe or dismiss.
 */
export const PaywallModal: React.FC<PaywallModalProps> = ({
	isOpen,
	isAuthenticated,
	onClose,
	onSubscribe,
	onLogin,
	onLoginGoogle,
	title,
}) => {
	const styles = useStyles();
	const isGoogleDocs = useEditor().host === "gdocs";

	const handleSubscribe = (priceTier: 'annual' | 'monthly') => {
		onSubscribe(priceTier);
		// Don't close the modal here - let the parent handle it after Stripe checkout
	};

	const handleLogin = () => {
		onClose();
		onLogin();
	};

	const handleLoginGoogle = () => {
		onClose();
		if (onLoginGoogle) onLoginGoogle();
	};

	if (!isAuthenticated) {
		return (
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title="Login Required"
				actions={
					<div className={styles.loginActions}>
						{!isGoogleDocs && (
							<Button appearance="primary" className={styles.microsoftBtn} onClick={handleLogin}>
								Sign in with Microsoft
							</Button>
						)}
						{(isGoogleDocs || featureFlags.googleAuth) && onLoginGoogle && (
							<Button appearance="primary" className={styles.googleBtn} onClick={handleLoginGoogle}>
								Sign in with Google
							</Button>
						)}
						<Button appearance="secondary" onClick={onClose}>
							Maybe Later
						</Button>
					</div>
				}
			>
				<div className={styles.content}>
					<Text className={styles.messageText}>
						Please login to add curriculum data to your document.
					</Text>
				</div>
			</Modal>
		);
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={title ?? "Trial Expired"}
			actions={
				<Button appearance="secondary" onClick={onClose}>
					Maybe Later
				</Button>
			}
		>
			<div className={styles.content}>
				<Text className={styles.messageText}>
					Your trial has expired. Choose a plan to continue inserting
					curriculum content into your documents.
				</Text>
				<div className={styles.planButtons}>
					<Button appearance="primary" onClick={() => handleSubscribe('annual')}>
						Annual — €20/year (best value)
					</Button>
					<Button appearance="outline" onClick={() => handleSubscribe('monthly')}>
						Monthly — €4.99/month
					</Button>
				</div>
			</div>
		</Modal>
	);
};

export default PaywallModal;
